// resilientSession — живая запись, которая переживает обрыв связи.
//
// Один объект на одну запись (от «Начать запись» до «Стоп»); под ним — сколько
// угодно серверных сеансов bit-streaming (services/streamingClient.js, один сокет —
// один сеанс). Обрыв соединения — не конец записи: микрофон продолжает писать,
// PCM копится в буфере, соединение восстанавливается с паузами 1, 2, 4, 8, 15, 15… с,
// накопленное уходит первым — не быстрее PACE_FACTOR реального времени, — затем
// живой поток. Текст нового сеанса приходит со сдвигом времени и дописывается
// в тот же документ.
//
// Звук молча не теряется. Отправленное хранится, пока сервер его не подтвердил
// (pong с audio_sec) и пока фраза, в которую оно входит, не стала final; после
// обрыва всё это уходит в новый сеанс заново. «Стоп» при непустом буфере — drain():
// микрофон уже молчит, накопленное досылается, и только потом finalize.
//
// Конец записи (onEnd) — только серверный отказ с причиной (квота, авторизация,
// протокол, темп, лимит длительности, простой, ошибка сервиса) или 5 минут без
// связи / полный буфер. Остановка человеком — drain()/finalize() + close(), onEnd не зовётся.
//
// Спека: docs/superpowers/specs/2026-09-29-live-reconnect-design.md
//
// Все проверки — в tick(): он крутится по интервалу И на каждом кадре микрофона.
// Таймеры скрытой вкладки браузер троттлит (до раза в минуту), а сообщения
// AudioWorklet приходят без троттлинга — так пейсинг, heartbeat и переподключение
// не встают, пока человек смотрит в окно видеовстречи.

export const SAMPLE_RATE = 16000;
// Паузы перед попытками переподключения, сек; дальше — последняя.
export const BACKOFF_SEC = [1, 2, 4, 8, 15];
// Сколько ждать связь, прежде чем сдаться, и потолок буфера (5 мин звука
// 16 кГц моно int16 ≈ 9,6 МБ). Буфер не растёт дальше: лучше остановиться.
export const MAX_OFFLINE_SEC = 300;
export const MAX_BUFFER_SEC = 300;
// Темп досылки. Сервер режет звук дальше pace_factor × wall + pace_burst_sec
// (1,5× и 8 с, bit-streaming/app/session.py) — берём с запасом на сетевой джиттер.
export const PACE_FACTOR = 1.4;
export const PACE_BURST_SEC = 2;
// Прикладной heartbeat: браузер не умеет WS-ping, поэтому мёртвое соединение
// (сокет открыт, байты уходят в никуда) замечаем сами. Пока досылаем буфер,
// сервер занят декодом пачки и отвечает позже — ждём дольше.
export const PING_EVERY_MS = 10000;
export const PONG_TIMEOUT_MS = 20000;
export const PONG_TIMEOUT_CATCHUP_MS = 60000;
// Подключение (и первое, и после обрыва), не дождавшееся кадра status, — неудача.
export const CONNECT_TIMEOUT_MS = 10000;
// Сколько ждать продления токена перед попыткой переподключения.
export const PREPARE_TIMEOUT_MS = 3000;
// Отказ 1008 на попытке, который может быть протухшим токеном (не квота и не
// протокол), повторяем ещё столько раз: токен успеет продлиться.
export const AUTH_RETRIES = 2;
const HARD_1008 = new Set(['quota_exhausted', 'pace_exceeded', 'frame_too_large', 'bad_message']);
// Сеанс, проживший дольше, сбрасывает паузы на начало: сервер, который рвёт
// каждое соединение через секунду, не долбим раз в секунду.
export const STABLE_SESSION_MS = 30000;
export const TICK_MS = 250;
// Сколько отправленного звука держим для досылки: неподтверждённое плюс
// недоговорённая фраза (у сервера она не длиннее max_utt_sec = 28 с). Пока pong
// приходят, этот хвост короткий; потолок — защита памяти, если их нет минуту.
const RETAIN_MAX_SEC = 120;
const PENDING_UTT_SEC = 30;    // недоговорённая фраза: до неё может тянуться хвост
const UTT_ONSET_SEC = 2;       // фраза могла начаться, а partial ещё не прийти

/**
 * Что значит закрытие сокета: 'refusal' — конец записи с сообщением,
 * 'retry' — переподключаемся, 'auth' — 1008 на попытке, похожий на протухший
 * токен: повторяем AUTH_RETRIES раз, потом отказ.
 *
 * stage:
 *  - 'first'   — первое подключение, сеанс ещё ни разу не установлен: как до
 *                этой правки, любое закрытие — отказ (сервис лежит — сказать сразу);
 *  - 'attempt' — попытка переподключения до кадра status: 1008 с причиной
 *                (квота, темп, протокол) — отказ; 1013 «занят» и 1011 ошибка
 *                проверки квоты на приёме — временные, повторяем;
 *  - 'live'    — установленный сеанс: отказы сервера с причиной, прочее — обрыв.
 */
export function classifyClose(code, reason = '', stage = 'live') {
  const r = reason || '';
  if (stage === 'first') return 'refusal';
  if (stage === 'attempt') {
    if (code !== 1008) return 'retry';
    return HARD_1008.has(r) ? 'refusal' : 'auth';
  }
  if (code === 1008 || code === 1011 || code === 1013) return 'refusal';
  if (code === 1000 && (r === 'max_session' || r.includes('idle'))) return 'refusal';
  // 1006 (обрыв), 1001 draining (перезапуск сервиса), 1000 без причины от прокси…
  return 'retry';
}

const isHiddenDefault = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * @param {object} opts
 * @param {(callbacks) => object} opts.connect — создать сокет-сеанс
 *   (createStreamingSession с URL и заголовками); колбэки: onStatus/onPartial/
 *   onFinal/onPong/onError/onClose. Клиент: sendPcm/finalize/close, опционально ping/isOpen.
 * @param {Function} [opts.onStatus] — кадр status каждого установленного сеанса.
 * @param {Function} [opts.onPartial] / [opts.onFinal] — текст; время final уже
 *   сдвинуто на начало сеанса внутри записи.
 * @param {Function} [opts.onError] — {type:'error'} от сервера.
 * @param {Function} [opts.onLink] — {state: 'connecting'|'live'|'reconnecting',
 *   catchingUp, backlogSec}: для плашки.
 * @param {Function} [opts.onEnd] — {code, reason, established, giveUp, cause,
 *   connectFailed}: запись окончена не по «Стопу».
 * @param {Function} [opts.beforeReconnect] — перед каждой попыткой (продлить
 *   токен); промис ждём до PREPARE_TIMEOUT_MS.
 * @param {Function} [opts.log] — (event, meta) телеметрия, без текста и токенов.
 */
export function createResilientSession({
  connect, onStatus, onPartial, onFinal, onError, onLink, onEnd,
  beforeReconnect, log, isHidden = isHiddenDefault, now = () => Date.now(),
}) {
  let closed = false;     // close() или конец записи: больше ничего не делаем
  let stopping = false;   // finalize отправлен (или отправлять некому): переподключений нет
  let draining = false;   // «Стоп» при непустом буфере: досылаем, потом finalize
  let drainDone = null;
  let paused = false;
  let conn = null;        // текущий сеанс (см. openConnection)

  // Буфер ещё не отправленного звука: [{pcm, pos}], pos — секунда записи.
  let queue = [];
  let queueSamples = 0;
  let capturedSamples = 0;  // сколько звука записано всего — ось времени записи
  let lastFinalEnd = null;  // конец последнего final по оси записи
  // Недоговорённая фраза сеанса, умершего на обрыве. Её звук вернулся в буфер и
  // распознается заново; сама гипотеза нужна, только если этого уже не случится.
  let orphanPartial = null;

  let everEstablished = false;
  let offlineSince = null;  // начало текущего обрыва
  let attempt = 0;          // номер попытки в текущем обрыве
  let authRetries = 0;
  let backoffIdx = 0;
  let nextAttemptAt = null;
  let preparing = false;    // ждём beforeReconnect перед попыткой
  let prepareTimer = null;
  let linkState = 'connecting';
  let catchingUp = false;
  let link = null;

  const queueSec = () => queueSamples / SAMPLE_RATE;
  const round1 = (x) => Math.round(x * 10) / 10;

  function emitLink() {
    const next = { state: linkState, catchingUp, backlogSec: Math.max(0, Math.ceil(queueSec() - 1e-9)) };
    if (link && link.state === next.state && link.catchingUp === next.catchingUp
      && link.backlogSec === next.backlogSec) return;
    link = next;
    onLink?.(link);
  }

  // --- сеанс ---------------------------------------------------------------

  function openConnection(stage) {
    const rec = {
      stage,
      ready: false,
      startedAt: now(),
      readyAt: null,
      // Сдвиг времени сеанса внутри записи: сервер считает время от своего первого
      // кадра, а первым уйдёт голова буфера. Уточняется в handleReady.
      offsetSec: queue.length ? queue[0].pos : capturedSamples / SAMPLE_RATE,
      t0: null,            // когда ушёл первый кадр — начало отсчёта темпа
      sentSec: 0,          // сколько звука ушло в этот сеанс (его собственная ось)
      lastSentEnd: null,   // конец последнего отправленного кадра по оси записи
      ackedSec: 0,         // сколько сервер подтвердил (pong.audio_sec)
      lastFinalEndRel: null, // конец последнего final по оси сеанса
      pendingPartial: '',
      lastAliveAt: null,
      lastPingAt: null,
      pingsInFlight: [],   // sentSec на момент каждого неотвеченного ping
      sent: [],            // отправленное, что ещё может понадобиться дослать
      client: null,
    };
    const isCurrent = () => conn === rec && !closed;
    const alive = () => { rec.lastAliveAt = now(); };
    conn = rec;
    rec.client = connect({
      onStatus: (msg) => { if (isCurrent()) handleReady(rec, msg); },
      onPartial: (msg) => {
        if (!isCurrent()) return;
        alive();
        orphanPartial = null;  // звук осиротевшей фразы распознаётся заново
        rec.pendingPartial = msg?.text || '';
        onPartial?.(msg);
      },
      onFinal: (msg) => {
        if (!isCurrent()) return;
        alive();
        orphanPartial = null;
        rec.pendingPartial = '';
        if (Number.isFinite(msg.end)) {
          // Final — тоже подтверждение: сервер разобрал звук как минимум до его конца.
          // Ось та же, что у ackedSec и sentSec, — время этого сеанса (до сдвига offsetSec).
          rec.lastFinalEndRel = msg.end;
          rec.ackedSec = Math.max(rec.ackedSec, msg.end);
        }
        const shift = (t) => (Number.isFinite(t) ? t + rec.offsetSec : t);
        const f = { ...msg, start: shift(msg.start), end: shift(msg.end) };
        if (Number.isFinite(f.end)) lastFinalEnd = f.end;
        trimSent(rec);
        onFinal?.(f);
      },
      onPong: (msg) => {
        if (!isCurrent()) return;
        alive();
        // Сервер разбирает кадры по порядку: pong подтверждает всё, что ушло до
        // этого ping. audio_sec — точное число; без него (старый сервер) — сколько
        // было отправлено к моменту ping.
        const atPing = rec.pingsInFlight.shift();
        const acked = Number.isFinite(msg?.audio_sec) ? msg.audio_sec : atPing;
        if (Number.isFinite(acked)) rec.ackedSec = Math.max(rec.ackedSec, acked);
        trimSent(rec);
      },
      onError: (msg) => {
        if (!isCurrent()) return;
        // Сетевая ошибка сокета всегда идёт вместе с закрытием — его и разбираем.
        if (msg?.code === 'ws_error') return;
        onError?.(msg);
      },
      onClose: (ev) => { if (isCurrent()) handleClose(rec, ev?.code, ev?.reason || ''); },
    });
    return rec;
  }

  // Отпустить отправленное, которое точно не понадобится: подтверждено сервером
  // и не входит в недоговорённую фразу (её звук нужен целиком, если сеанс умрёт
  // до final — иначе фраза пропала бы или задвоилась с гипотезой).
  function trimSent(rec) {
    const tail = rec.pendingPartial ? PENDING_UTT_SEC : UTT_ONSET_SEC;
    const keepFrom = Math.min(rec.ackedSec, Math.max(rec.lastFinalEndRel ?? 0, rec.ackedSec - tail));
    let i = 0;
    while (i < rec.sent.length && rec.sent[i].endSec <= keepFrom + 1e-3) i += 1;
    if (i) rec.sent = rec.sent.slice(i);
  }

  function retire(rec, { closeSocket = false } = {}) {
    if (conn === rec) conn = null;
    if (closeSocket) {
      try { rec.client?.close(); } catch (e) { /* сокет уже мёртв */ }
    }
  }

  function handleReady(rec, msg) {
    const t = now();
    const wasAttempt = rec.stage === 'attempt';
    rec.ready = true;
    rec.readyAt = t;
    rec.lastAliveAt = t;
    rec.lastPingAt = t;
    rec.offsetSec = queue.length ? queue[0].pos : capturedSamples / SAMPLE_RATE;
    rec.stage = 'live';
    everEstablished = true;
    if (wasAttempt) {
      log?.('live_reconnected', {
        attempt,
        offline_sec: round1((t - offlineSince) / 1000),
        buffered_sec: round1(queueSec()),
      });
    }
    offlineSince = null;
    attempt = 0;
    authRetries = 0;
    nextAttemptAt = null;
    linkState = 'live';
    catchingUp = wasAttempt && queue.length > 0;
    emitLink();
    onStatus?.(msg);
    pump();
  }

  function emitSalvaged(text, start, end) {
    lastFinalEnd = end;
    onFinal?.({ type: 'final', text, start, end, utt: null, salvaged: true });
  }

  // Звук этой гипотезы больше никто не распознает (конец записи) — лучше она, чем ничего.
  function salvageOrphan() {
    if (!orphanPartial) return;
    const { text } = orphanPartial;
    orphanPartial = null;
    const start = lastFinalEnd ?? 0;
    emitSalvaged(text, start, start);
  }

  function salvagePartial(rec) {
    const text = (rec.pendingPartial || '').trim();
    rec.pendingPartial = '';
    if (!text) return;
    const start = lastFinalEnd ?? rec.offsetSec;
    emitSalvaged(text, start, Math.max(start, rec.lastSentEnd ?? start));
  }

  // Сеанс умер, переподключаемся: всё, что может понадобиться, — обратно в голову
  // буфера. Гипотезу не публикуем: её звук распознается заново, иначе был бы дубль.
  function requeueSent(rec) {
    trimSent(rec);
    if (rec.sent.length) {
      const back = rec.sent.map(({ pcm, pos }) => ({ pcm, pos }));
      queue = back.concat(queue);
      queueSamples += back.reduce((n, u) => n + u.pcm.length, 0);
      rec.sent = [];
    }
    const text = (rec.pendingPartial || '').trim();
    if (text) orphanPartial = { text };
    rec.pendingPartial = '';
  }

  function handleClose(rec, code, reason) {
    retire(rec);
    if (stopping) return;  // «Стоп»: сокет закрылся в грейс-окне — так и надо
    const wasLive = rec.stage === 'live';
    if (wasLive) {
      log?.('live_disconnect', { code: code ?? null, reason: String(reason).slice(0, 64), hidden: !!isHidden() });
    }
    let kind = classifyClose(code, reason, rec.stage);
    if (kind === 'auth') {
      if (authRetries < AUTH_RETRIES) { authRetries += 1; kind = 'retry'; } else kind = 'refusal';
    }
    if (kind === 'refusal') {
      if (wasLive) salvagePartial(rec);
      end({ code, reason, established: everEstablished, giveUp: false });
      return;
    }
    if (wasLive) {
      requeueSent(rec);
      if (rec.readyAt !== null && now() - rec.readyAt >= STABLE_SESSION_MS) backoffIdx = 0;
    }
    beginOutage();
  }

  // Соединение молчит: байты уходят, ответа нет. Сокет бросаем, не дожидаясь
  // onclose — на мёртвой сети браузер закрывает его минутами.
  function handleDead(rec) {
    retire(rec, { closeSocket: true });
    log?.('live_disconnect', { code: null, reason: 'pong_timeout', hidden: !!isHidden() });
    requeueSent(rec);
    if (rec.readyAt !== null && now() - rec.readyAt >= STABLE_SESSION_MS) backoffIdx = 0;
    beginOutage();
  }

  function beginOutage() {
    if (offlineSince === null) offlineSince = now();
    onPartial?.({ type: 'partial', text: '' });  // гипотеза мёртвого сеанса больше не обновится
    linkState = 'reconnecting';
    catchingUp = false;
    emitLink();
    scheduleAttempt();
    checkDrain();
  }

  function scheduleAttempt() {
    const delay = BACKOFF_SEC[Math.min(backoffIdx, BACKOFF_SEC.length - 1)];
    backoffIdx += 1;
    nextAttemptAt = now() + delay * 1000;
  }

  // Попытка: сначала продлить токен (до PREPARE_TIMEOUT_MS), потом сокет — иначе
  // после долгого обрыва auth-кадр ушёл бы с протухшим токеном и получил 1008.
  function startAttempt() {
    nextAttemptAt = null;
    preparing = true;
    const go = () => {
      if (!preparing) return;
      preparing = false;
      clearTimeout(prepareTimer);
      prepareTimer = null;
      if (closed || stopping) return;
      attempt += 1;
      try {
        openConnection('attempt');
      } catch (e) {
        conn = null;  // сокет не создался (браузер отверг) — это просто неудачная попытка
        scheduleAttempt();
      }
    };
    let pending;
    try { pending = beforeReconnect?.(); } catch (e) { pending = null; }
    if (pending && typeof pending.then === 'function') {
      prepareTimer = setTimeout(go, PREPARE_TIMEOUT_MS);
      pending.then(go, go);
    } else {
      go();
    }
  }

  function end(info) {
    if (closed) return;
    salvageOrphan();
    shutdown();
    onEnd?.(info);
  }

  function shutdown() {
    closed = true;
    preparing = false;
    clearInterval(timer);
    clearTimeout(prepareTimer);
    queue = [];
    queueSamples = 0;
    if (conn) retire(conn, { closeSocket: true });
  }

  function giveUp() {
    // Буфер переполнился на живом сеансе — не связь пропала, а сервер не успевает.
    const lagging = !!(conn && conn.ready);
    if (lagging) log?.('live_backlog_overflow', { buffered_sec: round1(queueSec()) });
    else log?.('live_reconnect_failed', { offline_sec: round1((now() - (offlineSince ?? now())) / 1000) });
    end({ code: null, reason: '', established: everEstablished, giveUp: true, cause: lagging ? 'lagging' : 'offline' });
  }

  // «Стоп» дождался: буфер пуст. Живому сеансу — finalize; без связи отправлять
  // некому (сюда попадаем, только когда и досылать нечего).
  function checkDrain() {
    if (!draining || closed || stopping || queue.length) return;
    const c = conn;
    if (c && c.ready) {
      stopping = true;
      c.client.finalize();
    } else {
      stopping = true;
      nextAttemptAt = null;
      preparing = false;
      clearTimeout(prepareTimer);
      if (c) retire(c, { closeSocket: true });
      salvageOrphan();
    }
    const done = drainDone;
    drainDone = null;
    draining = false;
    done?.();
  }

  // --- отправка --------------------------------------------------------------

  function pump() {
    const c = conn;
    if (!c || !c.ready || closed || stopping) return;
    const t = now();
    const before = queue.length;
    while (queue.length) {
      if (c.client.isOpen && !c.client.isOpen()) break;  // кадр остаётся в буфере
      const item = queue[0];
      const sec = item.pcm.length / SAMPLE_RATE;
      if (c.t0 === null) c.t0 = t;
      const allowed = PACE_FACTOR * ((t - c.t0) / 1000) + PACE_BURST_SEC;
      if (c.sentSec + sec > allowed) break;
      queue.shift();
      queueSamples -= item.pcm.length;
      c.client.sendPcm(item.pcm);
      c.sentSec += sec;
      c.lastSentEnd = item.pos + sec;
      c.sent.push({ pcm: item.pcm, pos: item.pos, endSec: c.sentSec });
    }
    // Потолок памяти: без pong минуту и больше хвост не растёт бесконечно.
    let kept = c.sent.reduce((n, u) => n + u.pcm.length, 0);
    while (kept > RETAIN_MAX_SEC * SAMPLE_RATE) kept -= c.sent.shift().pcm.length;
    if (catchingUp && queue.length === 0) catchingUp = false;
    if (queue.length !== before || catchingUp || draining) emitLink();
    checkDrain();
  }

  function heartbeat(c, t) {
    if (paused || stopping || typeof c.client.ping !== 'function') return;
    if (t - c.lastPingAt >= PING_EVERY_MS) {
      c.client.ping();
      c.lastPingAt = t;
      c.pingsInFlight.push(c.sentSec);
    }
    const timeout = queue.length ? PONG_TIMEOUT_CATCHUP_MS : PONG_TIMEOUT_MS;
    if (c.pingsInFlight.length && t - c.lastAliveAt >= timeout) handleDead(c);
  }

  function tick() {
    if (closed) return;
    const t = now();
    const c = conn;
    if (c && c.ready) {
      pump();
      if (conn === c) heartbeat(c, t);
      return;
    }
    if (stopping) return;
    if (c && c.stage === 'first' && t - c.startedAt >= CONNECT_TIMEOUT_MS) {
      // Сервис не ответил на первое подключение — сказать сразу, а не через 5 минут.
      end({ code: null, reason: 'connect_timeout', established: false, giveUp: false, connectFailed: true });
      return;
    }
    if (offlineSince !== null && t - offlineSince >= MAX_OFFLINE_SEC * 1000) {
      giveUp();
      return;
    }
    if (c && c.stage === 'attempt' && t - c.startedAt >= CONNECT_TIMEOUT_MS) {
      retire(c, { closeSocket: true });
      scheduleAttempt();
      return;
    }
    if (!c && !preparing && nextAttemptAt !== null && t >= nextAttemptAt) startAttempt();
  }

  const timer = setInterval(tick, TICK_MS);
  try {
    openConnection('first');
  } catch (e) {
    // Первый сокет не создался — записи нет, таймер не оставляем висеть.
    closed = true;
    clearInterval(timer);
    throw e;
  }

  const api = {
    /** Кадр микрофона. Всегда через буфер: он же держит звук на время обрыва. */
    sendPcm(pcm) {
      if (closed || stopping || draining || !pcm) return;
      queue.push({ pcm, pos: capturedSamples / SAMPLE_RATE });
      capturedSamples += pcm.length;
      queueSamples += pcm.length;
      if (queueSec() > MAX_BUFFER_SEC) {
        giveUp();
        return;
      }
      if (linkState === 'reconnecting') emitLink();
      tick();
    },

    /** Есть ли что досылать: «Стоп» тогда не обрывает запись, а дописывает её (drain). */
    hasBacklog() {
      if (closed || stopping) return false;
      return queue.length > 0 || !(conn && conn.ready);
    },

    /**
     * «Стоп» с досылкой: микрофон уже остановлен, накопленное уходит в темпе
     * (с переподключениями, в тех же 5 минутах), затем finalize и onDone.
     * Нечего досылать — onDone сразу.
     */
    drain(onDone) {
      if (closed || stopping) { onDone?.(); return; }
      draining = true;
      drainDone = onDone;
      if (paused) api.setPaused(false);  // досылка идёт и после «Стопа» на паузе — heartbeat нужен
      emitLink();
      pump();
      checkDrain();
    },

    /**
     * «Стоп» сразу (и «Остановить сейчас»). Живой сеанс получает finalize
     * (финалы придут в грейс-окне); ещё не отправленный звук выбрасывается.
     * Попытки переподключения прекращаются.
     */
    finalize() {
      if (closed || stopping) return;
      pump();
      stopping = true;
      draining = false;
      drainDone = null;
      nextAttemptAt = null;
      preparing = false;
      clearTimeout(prepareTimer);
      queue = [];
      queueSamples = 0;
      const c = conn;
      if (c && c.ready) c.client.finalize();
      else {
        if (c) retire(c, { closeSocket: true });
        salvageOrphan();
      }
    },

    /** Закрыть всё: сокет, таймеры, буфер. Колбэки после этого не зовутся. */
    close() {
      if (closed) return;
      shutdown();
    },

    /**
     * Пауза: кадры не идут, ping тоже — простой сеанса сервер считает как прежде.
     * После паузы ping уходит сразу: его pong (audio_sec) подтвердит звук до паузы,
     * и старое не будет дослано повторно.
     */
    setPaused(value) {
      paused = !!value;
      if (!paused && conn && conn.ready) {
        const t = now();
        conn.lastAliveAt = t;
        conn.lastPingAt = t - PING_EVERY_MS;
        tick();
      }
    },
  };
  return api;
}
